import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Search,
  Download,
  Eye,
  Filter,
  Archive,
  FileText,
  Calendar,
  Upload,
  Plus,
  FolderPlus,
  FolderOpen,
  Folder as FolderIcon,
  ChevronRight,
  ArrowLeft,
  Trash2,
  Edit,
  Lock,
  Globe,
  Users,
  GraduationCap,
  Loader2,
  MoreVertical,
  X,
  UserPlus,
  Home,
  CheckSquare,
  Share2
} from 'lucide-react';
import { Checkbox } from '../ui/checkbox';
import { ArchiveRecord, DocumentFolder, User as UserType } from '../../types';
import { api } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../utils/supabase/client';
import { toast } from 'sonner';
import { CommentSection } from './CommentSection';
import { DocumentArchiveKMeans } from './DocumentArchiveKMeans';
import { ARCHIVE_TARGET_EVENT, type ArchiveTarget, clearArchiveTargetFromUrl, readArchiveTargetFromUrl } from '../../utils/notificationLinks';

// Helper: get file extension
const getFileExtension = (filename: string) => filename.split('.').pop()?.toLowerCase() || '';

// Helper: check if file is previewable
const isPreviewable = (filename: string) => {
  const ext = getFileExtension(filename);
  return ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext);
};

// Helper: check if file is an image
const isImageFile = (filename: string) => {
  const ext = getFileExtension(filename);
  return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext);
};

// Helper: check if file is a PDF
const isPdfFile = (filename: string) => getFileExtension(filename) === 'pdf';

// Inline PDF Preview: embeds PDF directly in an iframe.
// Supabase Storage serves PDFs with correct Content-Type natively so no blob conversion is needed.
function PdfInlinePreview({ fileUrl, title }: { fileUrl: string; title: string }) {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  return (
    <div className="w-full">
      {!loaded && !error && (
        <div className="w-full flex flex-col items-center justify-center gap-3 p-6">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Loading PDF preview…</p>
        </div>
      )}
      {error && (
        <div className="w-full flex flex-col items-center justify-center gap-3 p-6">
          <FileText className="h-12 w-12 text-red-500" />
          <p className="text-sm font-medium">PDF Document</p>
          <p className="text-xs text-muted-foreground text-center">
            Could not load inline preview. Click "Open in New Tab" below to view this PDF.
          </p>
        </div>
      )}
      <iframe
        src={fileUrl}
        title={title}
        className={`h-[38vh] w-full rounded border sm:h-[52vh] lg:h-[60vh] ${loaded ? '' : 'hidden'}`}
        onLoad={() => setLoaded(true)}
        onError={() => setError(true)}
      />
    </div>
  );
}

interface FolderDocumentManagerProps {
  userRole: 'administrator' | 'student' | 'faculty';
  currentUserId: string;
  currentUserName?: string;
  studentCourse?: string;
}

// Local folder/document state stored in KV (via archives API)
// Folders are stored as archives with type 'document' and a special content flag

export function FolderDocumentManager({ userRole, currentUserId, currentUserName, studentCourse }: FolderDocumentManagerProps) {
  const { accessToken } = useAuth();
  const [folders, setFolders] = useState<DocumentFolder[]>([]);
  const [documents, setDocuments] = useState<ArchiveRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentFolderId, setCurrentFolderId] = useState<string | undefined>(undefined);
  const [breadcrumb, setBreadcrumb] = useState<{ id?: string; name: string }[]>([{ name: 'Home' }]);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  // Dialog states
  const [isCreateFolderOpen, setIsCreateFolderOpen] = useState(false);
  const [isUploadDocOpen, setIsUploadDocOpen] = useState(false);
  const [isViewDocOpen, setIsViewDocOpen] = useState(false);
  const [viewingDocument, setViewingDocument] = useState<ArchiveRecord | null>(null);
  const [linkedCommentId, setLinkedCommentId] = useState<string | undefined>(undefined);
  const [pendingArchiveTarget, setPendingArchiveTarget] = useState<ArchiveTarget | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [editingFolder, setEditingFolder] = useState<DocumentFolder | null>(null);
  const [isEditFolderOpen, setIsEditFolderOpen] = useState(false);
  const [isEditDocOpen, setIsEditDocOpen] = useState(false);
  const [editingDocument, setEditingDocument] = useState<ArchiveRecord | null>(null);

  // Share document dialog state
  const [isShareDocOpen, setIsShareDocOpen] = useState(false);
  const [sharingDocument, setSharingDocument] = useState<ArchiveRecord | null>(null);
  const [shareTargetUsers, setShareTargetUsers] = useState<string[]>([]);

  // Faculty/Student lists for targeting
  const [facultyList, setFacultyList] = useState<any[]>([]);
  const [studentList, setStudentList] = useState<any[]>([]);
  // Admin list for Only Me folder sharing between administrators
  const [adminList, setAdminList] = useState<any[]>([]);
  // Course list for prospectus document filtering
  const [courseList, setCourseList] = useState<{ id: string; code: string; name: string }[]>([]);

  // Share folder dialog state (for sharing Only Me folders with other admins)
  const [isShareFolderOpen, setIsShareFolderOpen] = useState(false);
  const [sharingFolder, setSharingFolder] = useState<DocumentFolder | null>(null);
  const [shareFolderTargetUsers, setShareFolderTargetUsers] = useState<string[]>([]);

  // Folder form - non-admins default to only_me (only visible to creator)
  const [folderForm, setFolderForm] = useState({
    name: '',
    description: '',
    visibility: (userRole === 'administrator' ? 'public' : 'only_me') as 'public' | 'public_faculty' | 'public_students' | 'public_course' | 'private' | 'only_me',
    targetUsers: [] as string[],
    targetRoles: [] as string[],
    courseFilter: '' as string
  });

  // Document upload form - visibility is always inherited from the parent folder
  const [docForm, setDocForm] = useState({
    title: '',
    description: '',
    type: 'document' as ArchiveRecord['type'],
    targetUsers: [] as string[],
    courseFilter: '' as string,
    files: [] as File[]
  });

  // Edit document form
  const [editDocForm, setEditDocForm] = useState({
    title: '',
    description: '',
    courseFilter: '',
  });

  // Upload progress tracking for multi-file upload
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });

  // Multi-select state
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedFolderIds, setSelectedFolderIds] = useState<Set<string>>(new Set());
  const [selectedDocIds, setSelectedDocIds] = useState<Set<string>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Determine if current user can create subfolders / upload docs in the current folder
  // All administrators can create in any folder (not just their own)
  const currentFolder = folders.find(f => f.id === currentFolderId);
  const canCreateInFolder = !currentFolderId || currentFolder?.createdBy === currentUserId || userRole === 'administrator';

  // Document visibility is always inherited from the parent folder
  const inheritedVisibility: DocumentFolder['visibility'] | null = currentFolder?.visibility || null;

  // Helper: detect if a folder is a "Prospectus" folder (by name)
  const isProspectusFolderById = (folderId?: string): boolean => {
    if (!folderId) return false;
    const folder = folders.find(f => f.id === folderId);
    return folder ? folder.name.toLowerCase().includes('prospectus') : false;
  };
  const isCurrentFolderProspectus = currentFolder ? currentFolder.name.toLowerCase().includes('prospectus') : false;

  // Fetch data
  useEffect(() => {
    fetchData();
    setupRealtimeSubscription();
  }, [accessToken]);

  useEffect(() => {
    const initialTarget = readArchiveTargetFromUrl();
    if (initialTarget) {
      setPendingArchiveTarget(initialTarget);
    }

    const handleOpenArchiveTarget = (event: Event) => {
      const customEvent = event as CustomEvent<ArchiveTarget>;
      setPendingArchiveTarget(customEvent.detail);
    };

    window.addEventListener(ARCHIVE_TARGET_EVENT, handleOpenArchiveTarget as EventListener);
    return () => {
      window.removeEventListener(ARCHIVE_TARGET_EVENT, handleOpenArchiveTarget as EventListener);
    };
  }, []);

  // Fetch faculty/student lists for user targeting (all roles need this for sharing)
  useEffect(() => {
    fetchUsersForTargeting();
  }, [accessToken]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = accessToken || '';
      const [archivesRes, foldersRes] = await Promise.all([
        api.getArchives(token).catch(() => ({ archives: [] })),
        api.getFolders(token).catch(() => ({ folders: [] }))
      ]);
      setDocuments(archivesRes.archives || []);
      setFolders(foldersRes.folders || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsersForTargeting = async () => {
    try {
      const token = accessToken || '';
      // Use the directory endpoint (accessible by all authenticated users)
      // Use public courses endpoint for non-admins since /courses requires admin auth
      const [directoryRes, coursesRes, adminsRes] = await Promise.all([
        api.getUserDirectory(token).catch(() => ({ faculty: [], students: [] })),
        (userRole === 'administrator'
          ? api.getCourses(token).catch(() => ({ courses: [] }))
          : api.getPublicCourses().catch(() => ({ courses: [] }))
        ),
        (userRole === 'administrator'
          ? api.getAdmins(token).catch(() => ({ admins: [] }))
          : Promise.resolve({ admins: [] })
        )
      ]);
      setFacultyList(directoryRes.faculty || []);
      setStudentList(directoryRes.students || []);
      // Filter out self from admin list (no need to share with yourself)
      setAdminList((adminsRes.admins || []).filter((a: any) => a.authUserId !== currentUserId && a.id !== currentUserId));
      setCourseList((coursesRes.courses || []).map((c: any) => ({ id: c.id, code: c.code, name: c.name })));
    } catch (error) {
      console.error('Error fetching user lists:', error);
    }
  };

  const setupRealtimeSubscription = () => {
    const channel = supabase
      .channel('folder-doc-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'archives' }, () => {
        fetchData();
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'folders' }, () => {
        fetchData();
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  };

  // Build a mapping from record IDs to auth user IDs for targetUsers resolution
  // MUST be defined before canAccessFolder/canAccessDocument which use isCurrentUserInList
  const recordIdToAuthId = useMemo(() => {
    const map = new Map<string, string>();
    facultyList.forEach((f: any) => { if (f.id && f.authUserId) map.set(f.id, f.authUserId); });
    studentList.forEach((s: any) => { if (s.id && s.authUserId) map.set(s.id, s.authUserId); });
    return map;
  }, [facultyList, studentList]);

  // Helper: check if current user's auth ID is in a list of IDs (which may be record IDs or auth IDs)
  const isCurrentUserInList = (idList: string[] | undefined): boolean => {
    if (!idList || idList.length === 0) return false;
    for (const id of idList) {
      if (id === currentUserId) return true;
      const mapped = recordIdToAuthId.get(id);
      if (mapped === currentUserId) return true;
    }
    return false;
  };

  // Access filtering logic
  const canAccessFolder = (folder: DocumentFolder): boolean => {
    // Owner always has access
    if (folder.createdBy === currentUserId) return true;
    // 'only_me' folders are ONLY visible to the creator — unless shared with other admins via targetUsers
    // or the folder contains documents explicitly shared with this user
    if (folder.visibility === 'only_me') {
      if (userRole === 'administrator' && isCurrentUserInList(folder.targetUsers)) return true;
      // Allow access if any document inside this folder is shared with the current user
      const hasSharedDoc = documents.some(doc => {
        if (doc.folderId !== folder.id) return false;
        return isCurrentUserInList(doc.targetUsers) || isCurrentUserInList(doc.allowedUsers);
      });
      if (hasSharedDoc) return true;
      return false;
    }
    // Admin has access to everything except only_me
    if (userRole === 'administrator') return true;
    
    // Non-admin created folders
    if (folder.createdByRole !== 'administrator') {
      // Explicitly targeted users can see private/shared folders (resolves record IDs to auth IDs)
      if (isCurrentUserInList(folder.targetUsers)) return true;
      // Check role-based public sharing (Public All Faculty / Public All Students)
      if (folder.visibility === 'public_faculty' && userRole === 'faculty') return true;
      if (folder.visibility === 'public_students' && userRole === 'student') return true;
      // Public (Same Program) - only students in the same course/program can access
      if (folder.visibility === 'public_course') {
        if (userRole === 'student' && studentCourse && folder.courseFilter) {
          return folder.courseFilter.toLowerCase() === studentCourse.toLowerCase();
        }
        return false;
      }
      return false;
    }
    
    // Admin-created public folders
    if (folder.visibility === 'public') {
      if (folder.targetRoles && folder.targetRoles.length > 0) {
        return folder.targetRoles.includes(userRole);
      }
      return true;
    }
    // Public (All Faculty) folders
    if (folder.visibility === 'public_faculty') {
      return userRole === 'faculty';
    }
    // Public (All Students) folders
    if (folder.visibility === 'public_students') {
      return userRole === 'student';
    }
    // Public (Same Program) folders created by admin
    if (folder.visibility === 'public_course') {
      if (userRole === 'student' && studentCourse && folder.courseFilter) {
        return folder.courseFilter.toLowerCase() === studentCourse.toLowerCase();
      }
      return false;
    }
    // Admin-created private folders - check if user is targeted
    if (folder.visibility === 'private') {
      if (isCurrentUserInList(folder.targetUsers)) return true;
      if (folder.targetRoles && folder.targetRoles.includes(userRole)) return true;
      return false;
    }
    return false;
  };

  const canAccessDocument = (doc: ArchiveRecord): boolean => {
    // Owner always has access
    if (doc.userId === currentUserId || doc.uploadedBy === currentUserId) return true;
    
    // Documents inside 'only_me' folders are ONLY visible to the creator — unless shared with other admins via targetUsers
    // or the document itself is explicitly shared with the user
    if (doc.folderId) {
      const parentFolder = folders.find(f => f.id === doc.folderId);
      if (parentFolder?.visibility === 'only_me' && parentFolder.createdBy !== currentUserId) {
        // Allow access if the current admin is in the folder's targetUsers (shared)
        if (userRole === 'administrator' && isCurrentUserInList(parentFolder.targetUsers)) { /* allowed */ }
        // Allow access if the document itself is explicitly shared with this user
        else if (isCurrentUserInList(doc.targetUsers) || isCurrentUserInList(doc.allowedUsers)) { /* allowed */ }
        else return false;
      }
    }
    // Documents at root with no sharing info uploaded by non-admins are only_me
    if (!doc.folderId && !doc.isPublic && !(doc.allowedRoles && doc.allowedRoles.length > 0) && !(doc.targetUsers && doc.targetUsers.length > 0) && !(doc.allowedUsers && doc.allowedUsers.length > 0)) {
      const creatorRole = (doc as any).createdByRole;
      if (creatorRole && creatorRole !== 'administrator' && doc.uploadedBy !== currentUserId) return false;
    }
    
    // Admin has access to everything except only_me
    if (userRole === 'administrator') return true;
    // Targeted to this user explicitly (resolves record IDs to auth IDs)
    if (isCurrentUserInList(doc.targetUsers)) return true;
    if (isCurrentUserInList(doc.allowedUsers)) return true;
    if (doc.ownerId === currentUserId) return true;
    
    // Non-admin uploaded documents: check if shared via role-based public visibility
    const creatorRole = (doc as any).createdByRole;
    if (creatorRole && creatorRole !== 'administrator') {
      // Check role-based public sharing (Public All Faculty / Public All Students)
      if (doc.allowedRoles && doc.allowedRoles.includes(userRole)) return true;
      // Check course-based public sharing (Public Same Program)
      if (doc.allowedRoles && doc.allowedRoles.includes('same_course')) {
        if (userRole === 'student' && studentCourse && doc.courseFilter) {
          return doc.courseFilter.toLowerCase() === studentCourse.toLowerCase();
        }
        return false;
      }
      return false;
    }
    
    // For documents without createdByRole, check if it was uploaded by another non-admin user  
    if (!creatorRole && !doc.isPublic) return false;
    
    // Admin-created public documents
    if (doc.isPublic) return true;
    if (doc.allowedRoles && doc.allowedRoles.includes(userRole)) return true;
    return false;
  };

  const canManageItem = (item: DocumentFolder | ArchiveRecord): boolean => {
    if (userRole === 'administrator') return true;
    if ('createdBy' in item) return item.createdBy === currentUserId;
    return item.userId === currentUserId || item.uploadedBy === currentUserId;
  };

  // Filtered folders/documents for current view
  // NOTE: Server already filters folders by user access level (including record ID → auth ID resolution).
  // Client-side canAccessFolder is only applied for admins who have the full faculty/student lists.
  // For non-admins, the server is the source of truth for access control.
  const currentFolders = useMemo(() => {
    let filtered = folders
      .filter(f => f.parentId === currentFolderId || (!f.parentId && !currentFolderId));
    if (userRole === 'administrator') {
      filtered = filtered.filter(canAccessFolder);
    }

    if (searchTerm) {
      filtered = filtered.filter(f =>
        f.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (f.description || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    return filtered.sort((a, b) => a.name.localeCompare(b.name));
  }, [folders, currentFolderId, searchTerm, currentUserId, userRole]);

  const documentsInScope = useMemo(() => {
    let filtered = documents
      .filter(d => {
        if (currentFolderId) return d.folderId === currentFolderId;
        return !d.folderId;
      });
    if (userRole === 'administrator') {
      filtered = filtered.filter(canAccessDocument);
    }

    // Program-based filtering: when a student views a "Prospectus" folder,
    // only show documents whose courseFilter matches their enrolled course/program.
    if (userRole === 'student' && studentCourse && isCurrentFolderProspectus) {
      filtered = filtered.filter(d => {
        // If the document has no courseFilter set, hide it from students in prospectus folders
        // (admins should tag all prospectus docs with a course)
        if (!d.courseFilter) return false;
        // Match the student's course against the document's courseFilter
        return d.courseFilter.toLowerCase() === studentCourse.toLowerCase();
      });
    }

    return filtered.sort((a, b) => new Date(b.dateModified).getTime() - new Date(a.dateModified).getTime());
  }, [documents, currentFolderId, currentUserId, userRole, studentCourse, isCurrentFolderProspectus]);

  const currentDocuments = useMemo(() => {
    let filtered = documentsInScope;

    if (searchTerm) {
      filtered = filtered.filter(d =>
        d.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        d.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    return filtered.sort((a, b) => new Date(b.dateModified).getTime() - new Date(a.dateModified).getTime());
  }, [documentsInScope, searchTerm]);

  const accessibleDocuments = useMemo(() => {
    let filtered = [...documents];
    if (userRole === 'administrator') {
      filtered = filtered.filter(canAccessDocument);
    }

    return filtered.sort((a, b) => new Date(b.dateModified).getTime() - new Date(a.dateModified).getTime());
  }, [documents, currentUserId, userRole, folders, facultyList, studentList, studentCourse]);

  useEffect(() => {
    if (!pendingArchiveTarget || loading) {
      return;
    }

    const targetDocument = accessibleDocuments.find((document) => document.id === pendingArchiveTarget.documentId);

    if (!targetDocument) {
      clearArchiveTargetFromUrl();
      setPendingArchiveTarget(null);
      return;
    }

    setViewingDocument(targetDocument);
    setLinkedCommentId(pendingArchiveTarget.commentId);
    setIsViewDocOpen(true);
    setPendingArchiveTarget(null);
  }, [pendingArchiveTarget, loading, accessibleDocuments]);

  // Navigation
  const navigateToFolder = (folder: DocumentFolder) => {
    setCurrentFolderId(folder.id);
    setBreadcrumb(prev => [...prev, { id: folder.id, name: folder.name }]);
    setSearchTerm('');
  };

  const navigateToBreadcrumb = (index: number) => {
    const crumb = breadcrumb[index];
    setCurrentFolderId(crumb.id);
    setBreadcrumb(prev => prev.slice(0, index + 1));
    setSearchTerm('');
  };

  // Folder CRUD
  const handleCreateFolder = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const token = accessToken || '';
      const folderData: Partial<DocumentFolder> = {
        name: folderForm.name,
        description: folderForm.description,
        parentId: currentFolderId,
        createdBy: currentUserId,
        createdByRole: userRole,
        visibility: folderForm.visibility,
        targetUsers: folderForm.visibility === 'private' ? folderForm.targetUsers : folderForm.visibility === 'only_me' && userRole === 'administrator' ? folderForm.targetUsers : [],
        targetRoles: folderForm.visibility === 'public_faculty' ? ['faculty'] as ('student' | 'faculty' | 'administrator')[] : folderForm.visibility === 'public_students' ? ['student'] as ('student' | 'faculty' | 'administrator')[] : folderForm.targetRoles as ('student' | 'faculty' | 'administrator')[],
        courseFilter: folderForm.visibility === 'public_course' ? (folderForm.courseFilter || studentCourse || '') : undefined,
        dateCreated: new Date().toISOString(),
        dateModified: new Date().toISOString()
      };
      await api.createFolder(folderData, token);
      toast.success('Folder created successfully!');
      setIsCreateFolderOpen(false);
      resetFolderForm();
      fetchData();
      window.dispatchEvent(new CustomEvent('archiveChanged'));
    } catch (error) {
      console.error('Error creating folder:', error);
      toast.error('Failed to create folder');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteFolder = async (folderId: string) => {
    if (!confirm('Delete this folder and all its contents?')) return;
    try {
      const token = accessToken || '';
      await api.deleteFolder(folderId, token);
      toast.success('Folder deleted');
      fetchData();
      window.dispatchEvent(new CustomEvent('archiveChanged'));
    } catch (error) {
      console.error('Error deleting folder:', error);
      toast.error('Failed to delete folder');
    }
  };

  // Document CRUD
  const handleUploadDocument = async (e: React.FormEvent) => {
    e.preventDefault();
    if (docForm.files.length === 0) {
      toast.error('Please select at least one file');
      return;
    }
    setSubmitting(true);
    const token = accessToken || '';
    const effectiveVisibility = inheritedVisibility || 'only_me';
    const isPublic = effectiveVisibility === 'public' || effectiveVisibility === 'public_faculty' || effectiveVisibility === 'public_students' || effectiveVisibility === 'public_course';
    const totalFiles = docForm.files.length;
    let successCount = 0;
    let failCount = 0;

    setUploadProgress({ current: 0, total: totalFiles });
    toast.info(`Uploading ${totalFiles} file${totalFiles > 1 ? 's' : ''}...`);

    for (let i = 0; i < totalFiles; i++) {
      const file = docForm.files[i];
      setUploadProgress({ current: i + 1, total: totalFiles });
      try {
        const uploadResult = await api.uploadFile(file, token);
        if (!uploadResult.success || !uploadResult.url) {
          failCount++;
          console.error(`Upload failed for ${file.name}`);
          continue;
        }

        console.log(`File uploaded via ${uploadResult.storage}: ${uploadResult.url}`);

        // For single file, use the form title; for multiple files, use file name as title
        const fileTitle = totalFiles === 1 && docForm.title ? docForm.title : file.name.replace(/\.[^/.]+$/, '');
        const fileDescription = totalFiles === 1 ? docForm.description : docForm.description || file.name;

        const documentData = {
          title: fileTitle,
          description: fileDescription,
          type: docForm.type,
          tags: [],
          isPublic,
          accessLevel: isPublic ? 'public' : 'private',
          allowedRoles: effectiveVisibility === 'public_faculty' ? ['faculty'] : effectiveVisibility === 'public_students' ? ['student'] : effectiveVisibility === 'public_course' ? ['same_course'] : [],
          targetUsers: effectiveVisibility === 'private' ? (currentFolder?.targetUsers || []) : [],
          allowedUsers: effectiveVisibility === 'private' ? (currentFolder?.targetUsers || []) : [],
          folderId: currentFolderId || null,
          uploadedBy: currentUserId,
          createdByRole: userRole,
          fileName: file.name,
          fileSize: file.size,
          fileUrl: uploadResult.url,
          ...(docForm.courseFilter ? { courseFilter: docForm.courseFilter } : effectiveVisibility === 'public_course' && currentFolder?.courseFilter ? { courseFilter: currentFolder.courseFilter } : {}),
        };
        await api.createArchive(documentData, token);
        successCount++;
      } catch (error) {
        console.error(`Error uploading ${file.name}:`, error);
        failCount++;
      }
    }

    setUploadProgress({ current: 0, total: 0 });
    if (successCount > 0) {
      toast.success(`${successCount} document${successCount > 1 ? 's' : ''} uploaded successfully!${failCount > 0 ? ` (${failCount} failed)` : ''}`);
      setIsUploadDocOpen(false);
      resetDocForm();
      fetchData();
      window.dispatchEvent(new CustomEvent('archiveChanged'));
    } else {
      toast.error('All file uploads failed');
    }
    setSubmitting(false);
  };

  const handleDeleteDocument = async (docId: string) => {
    if (!confirm('Delete this document?')) return;
    try {
      const token = accessToken || '';
      await api.deleteArchive(docId, token);
      toast.success('Document deleted');
      fetchData();
      window.dispatchEvent(new CustomEvent('archiveChanged'));
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Failed to delete document');
    }
  };

  // Edit Folder
  const handleEditFolder = (folder: DocumentFolder) => {
    setEditingFolder(folder);
    setFolderForm({
      name: folder.name,
      description: folder.description || '',
      visibility: folder.visibility,
      targetUsers: folder.targetUsers || [],
      targetRoles: (folder.targetRoles || []).map(r => r as string),
      courseFilter: folder.courseFilter || '',
    });
    setIsEditFolderOpen(true);
  };

  const handleUpdateFolder = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingFolder) return;
    setSubmitting(true);
    try {
      const token = accessToken || '';
      const folderData: Partial<DocumentFolder> = {
        name: folderForm.name,
        description: folderForm.description,
        visibility: folderForm.visibility,
        targetUsers: folderForm.visibility === 'private' ? folderForm.targetUsers : folderForm.visibility === 'only_me' && userRole === 'administrator' ? folderForm.targetUsers : [],
        targetRoles: folderForm.visibility === 'public_faculty' ? ['faculty'] as ('student' | 'faculty' | 'administrator')[] : folderForm.visibility === 'public_students' ? ['student'] as ('student' | 'faculty' | 'administrator')[] : folderForm.targetRoles as ('student' | 'faculty' | 'administrator')[],
        courseFilter: folderForm.visibility === 'public_course' ? (folderForm.courseFilter || studentCourse || '') : undefined,
        dateModified: new Date().toISOString()
      };
      await api.updateFolder(editingFolder.id, folderData, token);
      toast.success('Folder updated successfully!');
      setIsEditFolderOpen(false);
      resetFolderForm();
      fetchData();
      window.dispatchEvent(new CustomEvent('archiveChanged'));
    } catch (error) {
      console.error('Error updating folder:', error);
      toast.error('Failed to update folder');
    } finally {
      setSubmitting(false);
    }
  };

  // Edit Document
  const handleEditDocument = (doc: ArchiveRecord) => {
    setEditingDocument(doc);
    setEditDocForm({
      title: doc.title,
      description: doc.description,
      courseFilter: doc.courseFilter || '',
    });
    setIsEditDocOpen(true);
  };

  const handleUpdateDocument = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingDocument) return;
    setSubmitting(true);
    try {
      const token = accessToken || '';
      const documentData = {
        title: editDocForm.title,
        description: editDocForm.description,
        tags: [],
        ...(editDocForm.courseFilter ? { courseFilter: editDocForm.courseFilter } : { courseFilter: null }),
        dateModified: new Date().toISOString(),
      };
      await api.updateArchive(editingDocument.id, documentData, token);
      toast.success('Document updated successfully!');
      setIsEditDocOpen(false);
      setEditingDocument(null);
      fetchData();
      window.dispatchEvent(new CustomEvent('archiveChanged'));
    } catch (error) {
      console.error('Error updating document:', error);
      toast.error('Failed to update document');
    } finally {
      setSubmitting(false);
    }
  };

  // Share Document
  const handleShareDocument = (doc: ArchiveRecord) => {
    setSharingDocument(doc);
    setShareTargetUsers(doc.targetUsers || doc.allowedUsers || []);
    setIsShareDocOpen(true);
  };

  const handleUpdateShareDocument = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!sharingDocument) return;
    setSubmitting(true);
    try {
      const token = accessToken || '';
      const documentData = {
        targetUsers: shareTargetUsers,
        allowedUsers: shareTargetUsers,
        accessLevel: shareTargetUsers.length > 0 ? 'private' : (sharingDocument.accessLevel || 'private'),
        dateModified: new Date().toISOString(),
      };
      await api.updateArchive(sharingDocument.id, documentData, token);
      toast.success(shareTargetUsers.length > 0 ? `Document shared with ${shareTargetUsers.length} user${shareTargetUsers.length > 1 ? 's' : ''}!` : 'Sharing settings updated!');
      setIsShareDocOpen(false);
      setSharingDocument(null);
      setShareTargetUsers([]);
      fetchData();
      window.dispatchEvent(new CustomEvent('archiveChanged'));
    } catch (error) {
      console.error('Error sharing document:', error);
      toast.error('Failed to update sharing settings');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleShareTargetUser = (userId: string) => {
    setShareTargetUsers(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  // Share Folder with other admins (for Only Me folders)
  const handleShareFolder = (folder: DocumentFolder) => {
    setSharingFolder(folder);
    setShareFolderTargetUsers(folder.targetUsers || []);
    setIsShareFolderOpen(true);
  };

  const handleUpdateShareFolder = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!sharingFolder) return;
    setSubmitting(true);
    try {
      const token = accessToken || '';
      const folderData: Partial<DocumentFolder> = {
        targetUsers: shareFolderTargetUsers,
        dateModified: new Date().toISOString(),
      };
      await api.updateFolder(sharingFolder.id, folderData, token);
      toast.success(shareFolderTargetUsers.length > 0 ? `Folder shared with ${shareFolderTargetUsers.length} administrator${shareFolderTargetUsers.length > 1 ? 's' : ''}!` : 'Sharing settings updated!');
      setIsShareFolderOpen(false);
      setSharingFolder(null);
      setShareFolderTargetUsers([]);
      fetchData();
      window.dispatchEvent(new CustomEvent('archiveChanged'));
    } catch (error) {
      console.error('Error sharing folder:', error);
      toast.error('Failed to update sharing settings');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleShareFolderTargetUser = (userId: string) => {
    setShareFolderTargetUsers(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  // Multi-select helpers
  const toggleSelectMode = () => {
    setIsSelectMode(prev => !prev);
    setSelectedFolderIds(new Set());
    setSelectedDocIds(new Set());
  };

  const toggleFolderSelection = (folderId: string) => {
    setSelectedFolderIds(prev => {
      const next = new Set(prev);
      if (next.has(folderId)) next.delete(folderId); else next.add(folderId);
      return next;
    });
  };

  const toggleDocSelection = (docId: string) => {
    setSelectedDocIds(prev => {
      const next = new Set(prev);
      if (next.has(docId)) next.delete(docId); else next.add(docId);
      return next;
    });
  };

  const selectableCurrentFolders = currentFolders.filter(canManageItem);
  const selectableCurrentDocs = currentDocuments.filter(canManageItem);

  const allSelectableSelected =
    (selectableCurrentFolders.length + selectableCurrentDocs.length) > 0 &&
    selectableCurrentFolders.every(f => selectedFolderIds.has(f.id)) &&
    selectableCurrentDocs.every(d => selectedDocIds.has(d.id));

  const totalSelected = selectedFolderIds.size + selectedDocIds.size;

  const toggleSelectAll = () => {
    if (allSelectableSelected) {
      setSelectedFolderIds(new Set());
      setSelectedDocIds(new Set());
    } else {
      setSelectedFolderIds(new Set(selectableCurrentFolders.map(f => f.id)));
      setSelectedDocIds(new Set(selectableCurrentDocs.map(d => d.id)));
    }
  };

  const handleBulkDelete = async () => {
    const folderCount = selectedFolderIds.size;
    const docCount = selectedDocIds.size;
    const msg = `Delete ${folderCount > 0 ? `${folderCount} folder${folderCount > 1 ? 's' : ''}` : ''}${folderCount > 0 && docCount > 0 ? ' and ' : ''}${docCount > 0 ? `${docCount} document${docCount > 1 ? 's' : ''}` : ''}? This cannot be undone.`;
    if (!confirm(msg)) return;

    setBulkDeleting(true);
    const token = accessToken || '';
    let successCount = 0;
    let failCount = 0;

    // Delete folders first
    for (const folderId of selectedFolderIds) {
      try {
        await api.deleteFolder(folderId, token);
        successCount++;
      } catch {
        failCount++;
      }
    }

    // Delete documents
    for (const docId of selectedDocIds) {
      try {
        await api.deleteArchive(docId, token);
        successCount++;
      } catch {
        failCount++;
      }
    }

    if (successCount > 0) {
      toast.success(`${successCount} item${successCount > 1 ? 's' : ''} deleted${failCount > 0 ? ` (${failCount} failed)` : ''}`);
      fetchData();
      window.dispatchEvent(new CustomEvent('archiveChanged'));
    } else {
      toast.error('Failed to delete items');
    }

    setSelectedFolderIds(new Set());
    setSelectedDocIds(new Set());
    setIsSelectMode(false);
    setBulkDeleting(false);
  };

  const handleDownload = async (doc: ArchiveRecord) => {
    const fileName = doc.fileName || doc.title;
    if (!doc.fileUrl) {
      toast.error('No file URL available for this document');
      return;
    }
    toast.info(`Downloading: ${fileName}`);
    console.log('Download URL:', doc.fileUrl);
    try {
      // Fetch file as blob to guarantee correct filename on save-as dialog
      // (browsers ignore the download attribute on cross-origin <a> links)
      const response = await fetch(doc.fileUrl);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (error) {
      console.error('Blob download failed, falling back to direct link:', error);
      window.open(doc.fileUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handlePreview = (doc: ArchiveRecord) => {
    if (!doc.fileUrl) {
      toast.error('No file available to preview');
      return;
    }
    console.log('Preview URL:', doc.fileUrl);
    // Supabase Storage & Cloudinary both serve files with correct Content-Type,
    // so we can open the URL directly — the browser will render PDFs/images natively.
    window.open(doc.fileUrl, '_blank', 'noopener,noreferrer');
  };

  // Form resets
  const resetFolderForm = () => {
    setFolderForm({ name: '', description: '', visibility: userRole === 'administrator' ? 'public' : 'only_me', targetUsers: [], targetRoles: [], courseFilter: '' } as typeof folderForm);
    setEditingFolder(null);
  };

  const resetDocForm = () => {
    setDocForm({ title: '', description: '', type: 'document', targetUsers: [], courseFilter: '', files: [] });
    setUploadProgress({ current: 0, total: 0 });
  };

  // Handle file selection from input
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    if (!fileList) return;
    const newFiles = Array.from(fileList);
    setDocForm(prev => ({ ...prev, files: [...prev.files, ...newFiles] }));
    // Reset input so the same files can be re-added if needed
    e.target.value = '';
  };

  // Remove a single file from selected files list
  const removeSelectedFile = (index: number) => {
    setDocForm(prev => ({ ...prev, files: prev.files.filter((_, i) => i !== index) }));
  };

  // Helpers
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getVisibilityBadge = (isPublic: boolean, targetUsers?: string[], allowedRoles?: string[], courseFilter?: string) => {
    if (isPublic && allowedRoles && allowedRoles.length > 0) {
      if (allowedRoles.includes('same_course') && courseFilter) {
        return <Badge variant="default" className="flex items-center gap-1 text-xs bg-purple-600"><GraduationCap className="h-3 w-3" /> {courseFilter}</Badge>;
      }
      if (allowedRoles.includes('faculty') && !allowedRoles.includes('student')) {
        return <Badge variant="default" className="flex items-center gap-1 text-xs bg-blue-600"><Users className="h-3 w-3" /> All Faculty</Badge>;
      }
      if (allowedRoles.includes('student') && !allowedRoles.includes('faculty')) {
        return <Badge variant="default" className="flex items-center gap-1 text-xs bg-green-600"><GraduationCap className="h-3 w-3" /> All Students</Badge>;
      }
    }
    if (isPublic) {
      return <Badge variant="default" className="flex items-center gap-1 text-xs"><Globe className="h-3 w-3" /> Public</Badge>;
    }
    if (targetUsers && targetUsers.length > 0) {
      return <Badge variant="secondary" className="flex items-center gap-1 text-xs"><UserPlus className="h-3 w-3" /> Shared ({targetUsers.length})</Badge>;
    }
    return <Badge variant="secondary" className="flex items-center gap-1 text-xs"><Lock className="h-3 w-3" /> Only Me</Badge>;
  };

  const getFolderVisibilityBadge = (folder: DocumentFolder) => {
    if (folder.visibility === 'public') {
      return <Badge variant="default" className="flex items-center gap-1 text-xs"><Globe className="h-3 w-3" /> Public</Badge>;
    }
    if (folder.visibility === 'public_faculty') {
      return <Badge variant="default" className="flex items-center gap-1 text-xs bg-blue-600"><Users className="h-3 w-3" /> All Faculty</Badge>;
    }
    if (folder.visibility === 'public_students') {
      return <Badge variant="default" className="flex items-center gap-1 text-xs bg-green-600"><GraduationCap className="h-3 w-3" /> All Students</Badge>;
    }
    if (folder.visibility === 'public_course') {
      return <Badge variant="default" className="flex items-center gap-1 text-xs bg-purple-600"><GraduationCap className="h-3 w-3" /> {folder.courseFilter || 'Same Program'}</Badge>;
    }
    if (folder.visibility === 'private' && folder.targetUsers && folder.targetUsers.length > 0) {
      return <Badge variant="secondary" className="flex items-center gap-1 text-xs"><UserPlus className="h-3 w-3" /> Shared ({folder.targetUsers.length} users)</Badge>;
    }
    if (folder.visibility === 'only_me' && folder.targetUsers && folder.targetUsers.length > 0) {
      return <Badge variant="secondary" className="flex items-center gap-1 text-xs"><Share2 className="h-3 w-3" /> Only Me · Shared ({folder.targetUsers.length})</Badge>;
    }
    return <Badge variant="secondary" className="flex items-center gap-1 text-xs"><Lock className="h-3 w-3" /> Only Me</Badge>;
  };

  const getUserNameById = (userId: string, doc?: ArchiveRecord): string => {
    // Use server-resolved name if available (works for all roles including admin uploaders)
    if (doc?.uploadedByName && (userId === doc.uploadedBy || userId === doc.userId)) {
      return doc.uploadedByName;
    }
    const faculty = facultyList.find(f => f.id === userId);
    if (faculty) return faculty.name;
    const student = studentList.find(s => s.id === userId);
    if (student) return student.name;
    if (userId === currentUserId) return currentUserName || 'You';
    return 'Unknown';
  };

  const allTargetableUsers = useMemo(() => {
    const users: { id: string; name: string; role: string; idNumber?: string }[] = [];
    facultyList.forEach(f => users.push({ id: f.id, name: f.name, role: 'Faculty', idNumber: f.idNumber }));
    studentList.forEach(s => users.push({ id: s.id, name: s.name, role: 'Student', idNumber: s.idNumber }));
    return users;
  }, [facultyList, studentList]);

  // Toggle user in target list
  const toggleTargetUser = (userId: string, list: 'folder' | 'doc') => {
    if (list === 'folder') {
      setFolderForm(prev => ({
        ...prev,
        targetUsers: prev.targetUsers.includes(userId)
          ? prev.targetUsers.filter(id => id !== userId)
          : [...prev.targetUsers, userId]
      }));
    } else {
      setDocForm(prev => ({
        ...prev,
        targetUsers: prev.targetUsers.includes(userId)
          ? prev.targetUsers.filter(id => id !== userId)
          : [...prev.targetUsers, userId]
      }));
    }
  };

  // User picker component
  const UserPicker = ({ selectedUsers, onToggle, label }: { selectedUsers: string[]; onToggle: (id: string) => void; label: string }) => {
    const [userSearch, setUserSearch] = useState('');
    const filteredUsers = allTargetableUsers.filter(u =>
      u.name.toLowerCase().includes(userSearch.toLowerCase()) ||
      (u.idNumber || '').toLowerCase().includes(userSearch.toLowerCase())
    );

    return (
      <div className="space-y-2">
        <Label>{label}</Label>
        <Input
          placeholder="Search users by name or ID..."
          value={userSearch}
          onChange={(e) => setUserSearch(e.target.value)}
          className="mb-2"
        />
        {selectedUsers.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {selectedUsers.map(uid => {
              const u = allTargetableUsers.find(x => x.id === uid);
              return (
                <Badge key={uid} variant="secondary" className="text-xs flex items-center gap-1">
                  {u?.name || uid}
                  <X className="h-3 w-3 cursor-pointer" onClick={() => onToggle(uid)} />
                </Badge>
              );
            })}
          </div>
        )}
        <div className="max-h-40 overflow-y-auto border rounded-md">
          {filteredUsers.length === 0 ? (
            <p className="text-xs text-muted-foreground p-2">No users found</p>
          ) : (
            filteredUsers.map(u => (
              <div
                key={u.id}
                className={`flex items-center justify-between px-3 py-2 hover:bg-muted cursor-pointer text-sm ${selectedUsers.includes(u.id) ? 'bg-muted' : ''}`}
                onClick={() => onToggle(u.id)}
              >
                <div>
                  <span className="font-medium">{u.name}</span>
                  {u.idNumber && <span className="text-xs text-muted-foreground ml-2">{u.idNumber}</span>}
                </div>
                <Badge variant="outline" className="text-xs">{u.role}</Badge>
              </div>
            ))
          )}
        </div>
      </div>
    );
  };

  // Admin picker component (for sharing Only Me folders/documents with other admins)
  const AdminPicker = ({ selectedUsers, onToggle, label }: { selectedUsers: string[]; onToggle: (id: string) => void; label: string }) => {
    const [adminSearch, setAdminSearch] = useState('');
    const filteredAdmins = adminList.filter((a: any) =>
      a.name.toLowerCase().includes(adminSearch.toLowerCase()) ||
      (a.idNumber || '').toLowerCase().includes(adminSearch.toLowerCase())
    );

    return (
      <div className="space-y-2">
        <Label>{label}</Label>
        <Input
          placeholder="Search administrators by name or ID..."
          value={adminSearch}
          onChange={(e) => setAdminSearch(e.target.value)}
          className="mb-2"
        />
        {selectedUsers.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {selectedUsers.map(uid => {
              const a = adminList.find((x: any) => x.id === uid || x.authUserId === uid);
              return (
                <Badge key={uid} variant="secondary" className="text-xs flex items-center gap-1">
                  {a?.name || uid}
                  <X className="h-3 w-3 cursor-pointer" onClick={() => onToggle(uid)} />
                </Badge>
              );
            })}
          </div>
        )}
        <div className="max-h-40 overflow-y-auto border rounded-md">
          {filteredAdmins.length === 0 ? (
            <p className="text-xs text-muted-foreground p-2">No other administrators found</p>
          ) : (
            filteredAdmins.map((a: any) => (
              <div
                key={a.id}
                className={`flex items-center justify-between px-3 py-2 hover:bg-muted cursor-pointer text-sm ${selectedUsers.includes(a.authUserId || a.id) ? 'bg-muted' : ''}`}
                onClick={() => onToggle(a.authUserId || a.id)}
              >
                <div>
                  <span className="font-medium">{a.name}</span>
                  {a.idNumber && <span className="text-xs text-muted-foreground ml-2">{a.idNumber}</span>}
                </div>
                <Badge variant="outline" className="text-xs">Administrator</Badge>
              </div>
            ))
          )}
        </div>
      </div>
    );
  };

  // Create Folder Dialog - rendered inline to preserve input focus
  const createFolderDialog = (
    <Dialog open={isCreateFolderOpen} onOpenChange={(open) => { setIsCreateFolderOpen(open); if (!open) resetFolderForm(); }}>
      <DialogContent className="w-[95vw] max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderPlus className="h-5 w-5" />
            Create New Folder
          </DialogTitle>
          <DialogDescription>
            Create a folder to organize your documents
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleCreateFolder} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="folder-name">Folder Name *</Label>
            <Input
              id="folder-name"
              value={folderForm.name}
              onChange={(e) => setFolderForm(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Enter folder name"
              required
              autoFocus
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="folder-desc">Description</Label>
            <Textarea
              id="folder-desc"
              value={folderForm.description}
              onChange={(e) => setFolderForm(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Optional description"
            />
          </div>
          <div className="space-y-2">
            <Label>Visibility</Label>
            <Select value={folderForm.visibility} onValueChange={(v: 'public' | 'public_faculty' | 'public_students' | 'public_course' | 'private' | 'only_me') => setFolderForm(prev => ({ ...prev, visibility: v, targetUsers: (v === 'private' || v === 'only_me') ? prev.targetUsers : [], courseFilter: v === 'public_course' ? (studentCourse || prev.courseFilter) : '' }))}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {userRole === 'administrator' && (
                  <SelectItem value="public">
                    <div className="flex items-center gap-2"><Globe className="h-4 w-4" /> Public - All users can see</div>
                  </SelectItem>
                )}
                <SelectItem value="public_faculty">
                  <div className="flex items-center gap-2"><Users className="h-4 w-4" /> Public (All Faculty)</div>
                </SelectItem>
                <SelectItem value="public_students">
                  <div className="flex items-center gap-2"><GraduationCap className="h-4 w-4" /> Public (All Students)</div>
                </SelectItem>
                {userRole === 'student' && studentCourse && (
                  <SelectItem value="public_course">
                    <div className="flex items-center gap-2"><GraduationCap className="h-4 w-4" /> Public (Same Program — {studentCourse})</div>
                  </SelectItem>
                )}
                {userRole === 'administrator' && (
                  <SelectItem value="public_course">
                    <div className="flex items-center gap-2"><GraduationCap className="h-4 w-4" /> Public (Specific Program)</div>
                  </SelectItem>
                )}
                <SelectItem value="private">
                  <div className="flex items-center gap-2"><UserPlus className="h-4 w-4" /> Shared - Specific users</div>
                </SelectItem>
                <SelectItem value="only_me">
                  <div className="flex items-center gap-2"><Lock className="h-4 w-4" /> Only Me</div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {folderForm.visibility === 'public_course' && (
            <div className="space-y-2">
              {userRole === 'student' && studentCourse ? (
                <div className="flex items-center gap-2 p-2 rounded-md border bg-muted/50">
                  <GraduationCap className="h-4 w-4 text-purple-600" />
                  <span className="text-sm">Only <strong>{studentCourse}</strong> students can access this folder</span>
                </div>
              ) : (
                <>
                  <Label htmlFor="folder-course-filter">Select Program / Course *</Label>
                  <Select
                    value={folderForm.courseFilter}
                    onValueChange={(v) => setFolderForm(prev => ({ ...prev, courseFilter: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a program/course" />
                    </SelectTrigger>
                    <SelectContent>
                      {courseList.map(course => (
                        <SelectItem key={course.id} value={course.code}>
                          {course.code} — {course.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </>
              )}
            </div>
          )}

          {folderForm.visibility === 'private' && (
            <UserPicker
              selectedUsers={folderForm.targetUsers}
              onToggle={(id) => toggleTargetUser(id, 'folder')}
              label="Share with specific users"
            />
          )}

          {folderForm.visibility === 'only_me' && userRole === 'administrator' && (
            <AdminPicker
              selectedUsers={folderForm.targetUsers}
              onToggle={(id) => toggleTargetUser(id, 'folder')}
              label="Share with other administrators (optional)"
            />
          )}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => { setIsCreateFolderOpen(false); resetFolderForm(); }}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create Folder
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );

  // Upload Document Dialog - rendered inline to preserve input focus
  const uploadDocumentDialog = (
    <Dialog open={isUploadDocOpen} onOpenChange={(open) => { setIsUploadDocOpen(open); if (!open) resetDocForm(); }}>
      <DialogContent className="w-[95vw] max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Upload Document
          </DialogTitle>
          <DialogDescription>
            Upload files or an entire folder{currentFolderId ? ' to the current folder' : ' to the root directory'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleUploadDocument} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="doc-title">Document Title {docForm.files.length <= 1 ? '*' : '(optional for multiple files)'}</Label>
            <Input
              id="doc-title"
              value={docForm.title}
              onChange={(e) => setDocForm(prev => ({ ...prev, title: e.target.value }))}
              placeholder={docForm.files.length > 1 ? "Leave blank to use file names as titles" : "Enter document title"}
              required={docForm.files.length <= 1}
              autoFocus
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="doc-desc">Description</Label>
            <Textarea
              id="doc-desc"
              value={docForm.description}
              onChange={(e) => setDocForm(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Enter description"
            />
          </div>

          {/* Course/Program selector for Prospectus folders */}
          {isCurrentFolderProspectus && (
            <div className="space-y-2">
              <Label htmlFor="doc-course-filter">Program / Course *</Label>
              <p className="text-xs text-muted-foreground">
                This is a Prospectus folder. Select the program this document belongs to so only students enrolled in that program can see it.
              </p>
              <Select
                value={docForm.courseFilter}
                onValueChange={(v) => setDocForm(prev => ({ ...prev, courseFilter: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a program/course" />
                </SelectTrigger>
                <SelectContent>
                  {courseList.map(course => (
                    <SelectItem key={course.id} value={course.code}>
                      {course.code} — {course.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <Label>Visibility</Label>
            <div className="flex items-center gap-2 p-2 rounded-md border bg-muted/50">
              {inheritedVisibility === 'public' && (
                <><Globe className="h-4 w-4 text-emerald-600" /><span className="text-sm">Public — inherited from folder</span></>
              )}
              {inheritedVisibility === 'public_faculty' && (
                <><Users className="h-4 w-4 text-blue-600" /><span className="text-sm">Public (All Faculty) — inherited from folder</span></>
              )}
              {inheritedVisibility === 'public_students' && (
                <><GraduationCap className="h-4 w-4 text-green-600" /><span className="text-sm">Public (All Students) — inherited from folder</span></>
              )}
              {inheritedVisibility === 'public_course' && (
                <><GraduationCap className="h-4 w-4 text-purple-600" /><span className="text-sm">Public ({currentFolder?.courseFilter || 'Same Program'} students only) — inherited from folder</span></>
              )}
              {inheritedVisibility === 'private' && (
                <><UserPlus className="h-4 w-4 text-orange-600" /><span className="text-sm">Shared with specific users — inherited from folder</span></>
              )}
              {inheritedVisibility === 'only_me' && (
                <><Lock className="h-4 w-4 text-gray-600" /><span className="text-sm">Only Me — inherited from folder</span></>
              )}
              {!inheritedVisibility && (
                <><Lock className="h-4 w-4 text-gray-600" /><span className="text-sm">Only Me — root level</span></>
              )}
            </div>
          </div>

          <div className="space-y-3">
            <Label>Select Files *</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <Label htmlFor="file-input" className="cursor-pointer">
                  <div className="border-2 border-dashed rounded-lg p-4 text-center hover:border-primary/50 hover:bg-muted/50 transition-colors">
                    <Upload className="h-6 w-6 mx-auto mb-1 text-muted-foreground" />
                    <p className="text-sm font-medium">Select Files</p>
                    <p className="text-xs text-muted-foreground">Click to browse multiple files</p>
                  </div>
                </Label>
                <input
                  id="file-input"
                  type="file"
                  className="hidden"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png"
                  multiple
                  onChange={handleFileSelect}
                />
              </div>
              <div>
                <Label htmlFor="folder-input" className="cursor-pointer">
                  <div className="border-2 border-dashed rounded-lg p-4 text-center hover:border-primary/50 hover:bg-muted/50 transition-colors">
                    <FolderOpen className="h-6 w-6 mx-auto mb-1 text-muted-foreground" />
                    <p className="text-sm font-medium">Upload Folder</p>
                    <p className="text-xs text-muted-foreground">Click to select an entire folder</p>
                  </div>
                </Label>
                <input
                  id="folder-input"
                  type="file"
                  className="hidden"
                  /* @ts-ignore - webkitdirectory is a non-standard attribute */
                  {...({ webkitdirectory: '', directory: '', mozdirectory: '' } as any)}
                  onChange={handleFileSelect}
                />
              </div>
            </div>

            {/* Selected files list */}
            {docForm.files.length > 0 && (
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium">{docForm.files.length} file{docForm.files.length > 1 ? 's' : ''} selected</p>
                  <Button type="button" variant="ghost" size="sm" className="text-xs h-6" onClick={() => setDocForm(prev => ({ ...prev, files: [] }))}>
                    Clear all
                  </Button>
                </div>
                <div className="max-h-32 overflow-y-auto border rounded-md divide-y">
                  {docForm.files.map((file, idx) => (
                    <div key={idx} className="flex items-center justify-between px-3 py-1.5 text-sm hover:bg-muted/50">
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                        <span className="truncate">{file.name}</span>
                        <span className="text-xs text-muted-foreground shrink-0">({formatFileSize(file.size)})</span>
                      </div>
                      <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0 shrink-0" onClick={() => removeSelectedFile(idx)}>
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Upload progress */}
          {submitting && uploadProgress.total > 1 && (
            <div className="space-y-1">
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Uploading file {uploadProgress.current} of {uploadProgress.total}</span>
                <span>{Math.round((uploadProgress.current / uploadProgress.total) * 100)}%</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div className="bg-primary h-2 rounded-full transition-all" style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }} />
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => { setIsUploadDocOpen(false); resetDocForm(); }}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting || docForm.files.length === 0}>
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              <Upload className="h-4 w-4 mr-2" />
              Upload{docForm.files.length > 1 ? ` (${docForm.files.length} files)` : ''}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );

  // View Document Dialog - rendered inline
  const viewDocumentDialog = viewingDocument ? (
      <Dialog open={isViewDocOpen} onOpenChange={(open) => {
        setIsViewDocOpen(open);
        if (!open) {
          setLinkedCommentId(undefined);
        }
      }}>
        <DialogContent className="flex max-h-[90vh] w-[95vw] max-w-4xl flex-col overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              {viewingDocument.title}
            </DialogTitle>
            <DialogDescription>{viewingDocument.description}</DialogDescription>
          </DialogHeader>
          <div className="min-h-0 space-y-4 overflow-y-auto pr-1">
            {/* Inline File Preview */}
            {viewingDocument.fileUrl && viewingDocument.fileName && isPreviewable(viewingDocument.fileName) && (() => {
              return (
                <div className="border rounded-lg overflow-hidden bg-muted/30">
                  {isImageFile(viewingDocument.fileName) ? (
                    <div className="flex justify-center p-4 max-h-[60vh]">
                      <img
                        src={viewingDocument.fileUrl}
                        alt={viewingDocument.title}
                        className="max-h-[56vh] max-w-full object-contain rounded"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          if (target.parentElement && !target.parentElement.querySelector('.fallback-msg')) {
                            const fallback = document.createElement('p');
                            fallback.className = 'text-sm text-muted-foreground p-4 text-center fallback-msg';
                            fallback.textContent = 'Image could not be loaded inline. Click "Open in New Tab" to view.';
                            target.parentElement.appendChild(fallback);
                          }
                        }}
                      />
                    </div>
                  ) : isPdfFile(viewingDocument.fileName) ? (
                    <PdfInlinePreview fileUrl={viewingDocument.fileUrl} title={viewingDocument.title} />
                  ) : null}
                </div>
              );
            })()}
            {viewingDocument.fileUrl && viewingDocument.fileName && !isPreviewable(viewingDocument.fileName) && (
              <div className="border rounded-lg p-6 bg-muted/30 text-center space-y-2">
                <FileText className="h-12 w-12 mx-auto text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  Preview not available for .{getFileExtension(viewingDocument.fileName)} files.
                </p>
                <p className="text-xs text-muted-foreground">Download the file to view its contents.</p>
              </div>
            )}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <p className="text-sm font-medium">Visibility</p>
                {getVisibilityBadge(viewingDocument.isPublic, viewingDocument.targetUsers, viewingDocument.allowedRoles, viewingDocument.courseFilter)}
              </div>
              <div>
                <p className="text-sm font-medium">Uploaded By</p>
                <p className="text-sm text-muted-foreground">{getUserNameById(viewingDocument.uploadedBy || viewingDocument.userId, viewingDocument)}</p>
              </div>
              {viewingDocument.fileName && (
                <div>
                  <p className="text-sm font-medium">File Name</p>
                  <p className="text-sm text-muted-foreground">{viewingDocument.fileName}</p>
                </div>
              )}
              {viewingDocument.fileSize && (
                <div>
                  <p className="text-sm font-medium">File Size</p>
                  <p className="text-sm text-muted-foreground">{formatFileSize(viewingDocument.fileSize)}</p>
                </div>
              )}
              <div>
                <p className="text-sm font-medium">Created</p>
                <p className="text-sm text-muted-foreground">{new Date(viewingDocument.dateCreated).toLocaleDateString()}</p>
              </div>
              <div>
                <p className="text-sm font-medium">Modified</p>
                <p className="text-sm text-muted-foreground">{new Date(viewingDocument.dateModified).toLocaleDateString()}</p>
              </div>
            </div>
            {viewingDocument.courseFilter && (
              <div>
                <p className="text-sm font-medium mb-2">Program / Course</p>
                <Badge variant="default" className="text-xs bg-indigo-600">
                  <GraduationCap className="h-3 w-3 mr-1" />
                  {viewingDocument.courseFilter}
                </Badge>
              </div>
            )}
            {!viewingDocument.isPublic && viewingDocument.targetUsers && viewingDocument.targetUsers.length > 0 && (userRole === 'administrator' || viewingDocument.uploadedBy === currentUserId) && (
              <div>
                <p className="text-sm font-medium mb-2">Targeted Users</p>
                <div className="flex flex-wrap gap-1">
                  {viewingDocument.targetUsers.map((uid, i) => (
                    <Badge key={i} variant="secondary" className="text-xs">{getUserNameById(uid)}</Badge>
                  ))}
                </div>
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              {viewingDocument.fileUrl && (
                <Button variant="outline" onClick={() => handlePreview(viewingDocument)}>
                  <Eye className="h-4 w-4 mr-2" />
                  Open in New Tab
                </Button>
              )}
              <Button onClick={() => handleDownload(viewingDocument)}>
                <Download className="h-4 w-4 mr-2" />
                Download
              </Button>
              {canManageItem(viewingDocument) && (
                <Button variant="outline" onClick={() => { setIsViewDocOpen(false); handleShareDocument(viewingDocument); }}>
                  <Share2 className="h-4 w-4 mr-2" />
                  Share
                </Button>
              )}
              {canManageItem(viewingDocument) && (
                <Button variant="outline" onClick={() => { setIsViewDocOpen(false); handleEditDocument(viewingDocument); }}>
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              )}
            </div>
            {/* Comments Section */}
            <div className="border-t pt-4 mt-2">
              <CommentSection
                documentId={viewingDocument.id}
                currentUserId={currentUserId}
                currentUserName={currentUserName || 'Unknown'}
                currentUserRole={userRole}
                focusCommentId={linkedCommentId}
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>
  ) : null;

  const openDocumentView = (doc: ArchiveRecord) => {
    setLinkedCommentId(undefined);
    setViewingDocument(doc);
    setIsViewDocOpen(true);
  };

  // Edit Folder Dialog
  const editFolderDialog = (
    <Dialog open={isEditFolderOpen} onOpenChange={(open) => { setIsEditFolderOpen(open); if (!open) resetFolderForm(); }}>
      <DialogContent className="w-[95vw] max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit className="h-5 w-5" />
            Edit Folder
          </DialogTitle>
          <DialogDescription>
            Update folder details and visibility settings
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleUpdateFolder} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit-folder-name">Folder Name *</Label>
            <Input
              id="edit-folder-name"
              value={folderForm.name}
              onChange={(e) => setFolderForm(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Enter folder name"
              required
              autoFocus
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-folder-desc">Description</Label>
            <Textarea
              id="edit-folder-desc"
              value={folderForm.description}
              onChange={(e) => setFolderForm(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Optional description"
            />
          </div>
          <div className="space-y-2">
            <Label>Visibility</Label>
            <Select value={folderForm.visibility} onValueChange={(v: 'public' | 'public_faculty' | 'public_students' | 'public_course' | 'private' | 'only_me') => setFolderForm(prev => ({ ...prev, visibility: v, targetUsers: (v === 'private' || v === 'only_me') ? prev.targetUsers : [], courseFilter: v === 'public_course' ? (studentCourse || prev.courseFilter) : '' }))}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {userRole === 'administrator' && (
                  <SelectItem value="public">
                    <div className="flex items-center gap-2"><Globe className="h-4 w-4" /> Public - All users can see</div>
                  </SelectItem>
                )}
                <SelectItem value="public_faculty">
                  <div className="flex items-center gap-2"><Users className="h-4 w-4" /> Public (All Faculty)</div>
                </SelectItem>
                <SelectItem value="public_students">
                  <div className="flex items-center gap-2"><GraduationCap className="h-4 w-4" /> Public (All Students)</div>
                </SelectItem>
                {userRole === 'student' && studentCourse && (
                  <SelectItem value="public_course">
                    <div className="flex items-center gap-2"><GraduationCap className="h-4 w-4" /> Public (Same Program — {studentCourse})</div>
                  </SelectItem>
                )}
                {userRole === 'administrator' && (
                  <SelectItem value="public_course">
                    <div className="flex items-center gap-2"><GraduationCap className="h-4 w-4" /> Public (Specific Program)</div>
                  </SelectItem>
                )}
                <SelectItem value="private">
                  <div className="flex items-center gap-2"><UserPlus className="h-4 w-4" /> Shared - Specific users</div>
                </SelectItem>
                <SelectItem value="only_me">
                  <div className="flex items-center gap-2"><Lock className="h-4 w-4" /> Only Me</div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {folderForm.visibility === 'public_course' && (
            <div className="space-y-2">
              {userRole === 'student' && studentCourse ? (
                <div className="flex items-center gap-2 p-2 rounded-md border bg-muted/50">
                  <GraduationCap className="h-4 w-4 text-purple-600" />
                  <span className="text-sm">Only <strong>{studentCourse}</strong> students can access this folder</span>
                </div>
              ) : (
                <>
                  <Label htmlFor="edit-folder-course-filter">Select Program / Course *</Label>
                  <Select
                    value={folderForm.courseFilter}
                    onValueChange={(v) => setFolderForm(prev => ({ ...prev, courseFilter: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a program/course" />
                    </SelectTrigger>
                    <SelectContent>
                      {courseList.map(course => (
                        <SelectItem key={course.id} value={course.code}>
                          {course.code} — {course.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </>
              )}
            </div>
          )}

          {folderForm.visibility === 'private' && (
            <UserPicker
              selectedUsers={folderForm.targetUsers}
              onToggle={(id) => toggleTargetUser(id, 'folder')}
              label="Share with specific users"
            />
          )}

          {folderForm.visibility === 'only_me' && userRole === 'administrator' && (
            <AdminPicker
              selectedUsers={folderForm.targetUsers}
              onToggle={(id) => toggleTargetUser(id, 'folder')}
              label="Share with other administrators (optional)"
            />
          )}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => { setIsEditFolderOpen(false); resetFolderForm(); }}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );

  // Edit Document Dialog
  const isEditDocProspectus = editingDocument?.folderId ? isProspectusFolderById(editingDocument.folderId) : false;
  const editDocumentDialog = editingDocument ? (
    <Dialog open={isEditDocOpen} onOpenChange={(open) => { setIsEditDocOpen(open); if (!open) setEditingDocument(null); }}>
      <DialogContent className="w-[95vw] max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit className="h-5 w-5" />
            Edit Document
          </DialogTitle>
          <DialogDescription>
            Update document details
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleUpdateDocument} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit-doc-title">Document Title *</Label>
            <Input
              id="edit-doc-title"
              value={editDocForm.title}
              onChange={(e) => setEditDocForm(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Enter document title"
              required
              autoFocus
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-doc-desc">Description</Label>
            <Textarea
              id="edit-doc-desc"
              value={editDocForm.description}
              onChange={(e) => setEditDocForm(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Enter description"
            />
          </div>
          {isEditDocProspectus && (
            <div className="space-y-2">
              <Label htmlFor="edit-doc-course-filter">Program / Course</Label>
              <Select
                value={editDocForm.courseFilter}
                onValueChange={(v) => setEditDocForm(prev => ({ ...prev, courseFilter: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a program/course" />
                </SelectTrigger>
                <SelectContent>
                  {courseList.map(course => (
                    <SelectItem key={course.id} value={course.code}>
                      {course.code} — {course.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {editingDocument.fileName && (
            <div className="space-y-1">
              <Label>Current File</Label>
              <div className="flex items-center gap-2 p-2 rounded-md border bg-muted/50">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">{editingDocument.fileName}</span>
                {editingDocument.fileSize && (
                  <span className="text-xs text-muted-foreground">({formatFileSize(editingDocument.fileSize)})</span>
                )}
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => { setIsEditDocOpen(false); setEditingDocument(null); }}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  ) : null;

  // Share Document Dialog
  const shareDocumentDialog = sharingDocument ? (
    <Dialog open={isShareDocOpen} onOpenChange={(open) => { setIsShareDocOpen(open); if (!open) { setSharingDocument(null); setShareTargetUsers([]); } }}>
      <DialogContent className="w-[95vw] max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5" />
            Share Document
          </DialogTitle>
          <DialogDescription>
            Share "{sharingDocument.title}" with specific users
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleUpdateShareDocument} className="space-y-4">
          <div className="space-y-1">
            <Label>Document</Label>
            <div className="flex items-center gap-2 p-2 rounded-md border bg-muted/50">
              <FileText className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">{sharingDocument.title}</span>
            </div>
          </div>

          <UserPicker
            selectedUsers={shareTargetUsers}
            onToggle={toggleShareTargetUser}
            label="Share with specific users"
          />

          {shareTargetUsers.length > 0 && (
            <div className="flex items-center gap-2 p-2 rounded-md border bg-blue-50 dark:bg-blue-950/30">
              <UserPlus className="h-4 w-4 text-blue-600" />
              <span className="text-sm">This document will be shared with <strong>{shareTargetUsers.length}</strong> user{shareTargetUsers.length > 1 ? 's' : ''}</span>
            </div>
          )}

          {shareTargetUsers.length === 0 && (sharingDocument.targetUsers?.length ?? 0) > 0 && (
            <div className="flex items-center gap-2 p-2 rounded-md border bg-amber-50 dark:bg-amber-950/30">
              <Lock className="h-4 w-4 text-amber-600" />
              <span className="text-sm">Removing all users will make this document private (only you can access it)</span>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => { setIsShareDocOpen(false); setSharingDocument(null); setShareTargetUsers([]); }}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              <Share2 className="h-4 w-4 mr-2" />
              {shareTargetUsers.length > 0 ? `Share with ${shareTargetUsers.length} user${shareTargetUsers.length > 1 ? 's' : ''}` : 'Update Sharing'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  ) : null;

  // Share Folder Dialog (for sharing Only Me folders with other admins)
  const shareFolderDialog = sharingFolder ? (
    <Dialog open={isShareFolderOpen} onOpenChange={(open) => { setIsShareFolderOpen(open); if (!open) { setSharingFolder(null); setShareFolderTargetUsers([]); } }}>
      <DialogContent className="w-[95vw] max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5" />
            Share Folder with Administrators
          </DialogTitle>
          <DialogDescription>
            Share "{sharingFolder.name}" with other administrators
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleUpdateShareFolder} className="space-y-4">
          <div className="space-y-1">
            <Label>Folder</Label>
            <div className="flex items-center gap-2 p-2 rounded-md border bg-muted/50">
              <FolderIcon className="h-4 w-4 text-amber-500" />
              <span className="text-sm font-medium">{sharingFolder.name}</span>
            </div>
          </div>

          <AdminPicker
            selectedUsers={shareFolderTargetUsers}
            onToggle={toggleShareFolderTargetUser}
            label="Share with other administrators"
          />

          {shareFolderTargetUsers.length > 0 && (
            <div className="flex items-center gap-2 p-2 rounded-md border bg-blue-50 dark:bg-blue-950/30">
              <UserPlus className="h-4 w-4 text-blue-600" />
              <span className="text-sm">This folder and its documents will be accessible to <strong>{shareFolderTargetUsers.length}</strong> administrator{shareFolderTargetUsers.length > 1 ? 's' : ''}</span>
            </div>
          )}

          {shareFolderTargetUsers.length === 0 && (sharingFolder.targetUsers?.length ?? 0) > 0 && (
            <div className="flex items-center gap-2 p-2 rounded-md border bg-amber-50 dark:bg-amber-950/30">
              <Lock className="h-4 w-4 text-amber-600" />
              <span className="text-sm">Removing all administrators will make this folder visible only to you</span>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => { setIsShareFolderOpen(false); setSharingFolder(null); setShareFolderTargetUsers([]); }}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              <Share2 className="h-4 w-4 mr-2" />
              {shareFolderTargetUsers.length > 0 ? `Share with ${shareFolderTargetUsers.length} admin${shareFolderTargetUsers.length > 1 ? 's' : ''}` : 'Update Sharing'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  ) : null;

  // Summary stats
  const totalAccessibleDocs = documents.filter(canAccessDocument).length;
  const publicDocs = documents.filter(d => d.isPublic && canAccessDocument(d)).length;
  const privateDocs = documents.filter(d => !d.isPublic && canAccessDocument(d)).length;
  const myDocs = documents.filter(d => d.userId === currentUserId || d.uploadedBy === currentUserId).length;

  const getRoleDescription = () => {
    switch (userRole) {
      case 'administrator': return 'Create folders, upload documents as public or private with targeted access';
      case 'faculty': return 'View shared documents, create your own folders and upload your files';
      case 'student': return 'View and download documents shared with you, manage your own files';
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <Loader2 className="h-12 w-12 mx-auto text-muted-foreground mb-4 animate-spin" />
          <p className="text-sm text-muted-foreground">Loading documents...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-xl md:text-2xl font-bold flex items-center gap-2">
            <Archive className="h-6 w-6" />
            Document Archive
          </h2>
          <p className="text-sm text-muted-foreground">{getRoleDescription()}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant={isSelectMode ? 'default' : 'outline'} onClick={toggleSelectMode}>
            <CheckSquare className="h-4 w-4 mr-2" />
            {isSelectMode ? 'Cancel' : 'Select'}
          </Button>
          {canCreateInFolder && !isSelectMode && (
            <Button variant="outline" onClick={() => setIsCreateFolderOpen(true)}>
              <FolderPlus className="h-4 w-4 mr-2" />
              New Folder
            </Button>
          )}
          {canCreateInFolder && !isSelectMode && (
            <Button onClick={() => setIsUploadDocOpen(true)}>
              <Upload className="h-4 w-4 mr-2" />
              Upload Document
            </Button>
          )}
        </div>
      </div>

      {/* Multi-select action bar */}
      {isSelectMode && (
        <div className="flex items-center gap-3 p-3 bg-muted/50 border rounded-lg">
          <Checkbox
            checked={allSelectableSelected}
            onCheckedChange={toggleSelectAll}
            id="select-all"
          />
          <label htmlFor="select-all" className="text-sm font-medium cursor-pointer select-none">
            Select All
          </label>
          <span className="text-sm text-muted-foreground">
            {totalSelected > 0 ? `${totalSelected} selected` : 'None selected'}
          </span>
          <div className="ml-auto">
            <Button
              variant="destructive"
              size="sm"
              disabled={totalSelected === 0 || bulkDeleting}
              onClick={handleBulkDelete}
            >
              {bulkDeleting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Delete Selected ({totalSelected})
            </Button>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className={`grid gap-4 ${userRole === 'administrator' ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-2'}`}>
        {userRole === 'administrator' && (
          <Card>
            <CardContent className="pt-4 pb-4 text-center">
              <div className="text-2xl font-bold">{totalAccessibleDocs}</div>
              <p className="text-xs text-muted-foreground">Total Documents</p>
            </CardContent>
          </Card>
        )}
        {userRole === 'administrator' && (
          <Card>
            <CardContent className="pt-4 pb-4 text-center">
              <div className="text-2xl font-bold text-green-600">{publicDocs}</div>
              <p className="text-xs text-muted-foreground">Public</p>
            </CardContent>
          </Card>
        )}
        <Card>
          <CardContent className="pt-4 pb-4 text-center">
            <div className="text-2xl font-bold text-amber-600">{privateDocs}</div>
            <p className="text-xs text-muted-foreground">Private</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4 text-center">
            <div className="text-2xl font-bold text-blue-600">{myDocs}</div>
            <p className="text-xs text-muted-foreground">My Documents</p>
          </CardContent>
        </Card>
      </div>

      {/* Breadcrumb Navigation */}
      <Card>
        <CardContent className="py-3 px-4">
          <div className="flex items-center gap-1 flex-wrap">
            {breadcrumb.map((crumb, idx) => (
              <div key={idx} className="flex items-center">
                {idx > 0 && <ChevronRight className="h-4 w-4 text-muted-foreground mx-1" />}
                <Button
                  variant={idx === breadcrumb.length - 1 ? "secondary" : "ghost"}
                  size="sm"
                  className="text-sm h-7"
                  onClick={() => navigateToBreadcrumb(idx)}
                >
                  {idx === 0 ? <Home className="h-3 w-3 mr-1" /> : <FolderOpen className="h-3 w-3 mr-1" />}
                  {crumb.name}
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Search & Filter Bar */}
      <div className="flex flex-col md:flex-row gap-3">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search folders and documents..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex gap-2">
          <Button variant={viewMode === 'grid' ? 'default' : 'outline'} size="sm" onClick={() => setViewMode('grid')}>Grid</Button>
          <Button variant={viewMode === 'list' ? 'default' : 'outline'} size="sm" onClick={() => setViewMode('list')}>List</Button>
        </div>
      </div>

      {/* Back button when in subfolder */}
      {currentFolderId && (
        <Button variant="ghost" size="sm" onClick={() => navigateToBreadcrumb(breadcrumb.length - 2)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
      )}

      {/* Content */}
      {viewMode === 'grid' ? (
        <div className="space-y-6">
          {/* Folders */}
          {currentFolders.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                <FolderOpen className="h-4 w-4" />
                Folders ({currentFolders.length})
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {currentFolders.map(folder => (
                  <Card
                    key={folder.id}
                    className={`group cursor-pointer hover:shadow-md transition-all hover:border-primary/50 ${isSelectMode && selectedFolderIds.has(folder.id) ? 'ring-2 ring-primary border-primary' : ''}`}
                    onClick={() => isSelectMode ? (canManageItem(folder) && toggleFolderSelection(folder.id)) : navigateToFolder(folder)}
                  >
                    <CardContent className="pt-4 pb-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          {isSelectMode && canManageItem(folder) && (
                            <Checkbox
                              checked={selectedFolderIds.has(folder.id)}
                              onCheckedChange={() => toggleFolderSelection(folder.id)}
                              onClick={(e) => e.stopPropagation()}
                              className="shrink-0"
                            />
                          )}
                          <div className="p-2 rounded-lg bg-amber-100 text-amber-600 group-hover:bg-amber-200 transition-colors">
                            <FolderIcon className="h-5 w-5" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-sm truncate">{folder.name}</p>
                            {folder.description && (
                              <p className="text-xs text-muted-foreground truncate">{folder.description}</p>
                            )}
                          </div>
                        </div>
                        {!isSelectMode && canManageItem(folder) && (
                          <div className="flex gap-1 opacity-0 group-hover:opacity-100">
                            {folder.visibility === 'only_me' && userRole === 'administrator' && folder.createdBy === currentUserId && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0"
                                onClick={(e) => { e.stopPropagation(); handleShareFolder(folder); }}
                                title="Share with other admins"
                              >
                                <Share2 className="h-3 w-3" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              onClick={(e) => { e.stopPropagation(); handleEditFolder(folder); }}
                              title="Edit folder"
                            >
                              <Edit className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              onClick={(e) => { e.stopPropagation(); handleDeleteFolder(folder.id); }}
                            >
                              <Trash2 className="h-3 w-3 text-destructive" />
                            </Button>
                          </div>
                        )}
                      </div>
                      <div className="mt-2 flex items-center gap-2">
                        {getFolderVisibilityBadge(folder)}
                        <span className="text-xs text-muted-foreground">
                          {new Date(folder.dateCreated).toLocaleDateString()}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Documents */}
          {currentDocuments.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Documents ({currentDocuments.length})
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {currentDocuments.map(doc => (
                  <Card key={doc.id} className={`hover:shadow-md transition-shadow ${isSelectMode && selectedDocIds.has(doc.id) ? 'ring-2 ring-primary border-primary' : ''}`}>
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          {isSelectMode && canManageItem(doc) && (
                            <Checkbox
                              checked={selectedDocIds.has(doc.id)}
                              onCheckedChange={() => toggleDocSelection(doc.id)}
                              className="shrink-0"
                            />
                          )}
                          <div className="p-2 rounded-full bg-blue-100 text-blue-600">
                            <FileText className="h-4 w-4" />
                          </div>
                          {getVisibilityBadge(doc.isPublic, doc.targetUsers, doc.allowedRoles, doc.courseFilter)}
                        </div>
                        {!isSelectMode && canManageItem(doc) && (
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              onClick={() => handleShareDocument(doc)}
                              title="Share document"
                            >
                              <Share2 className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              onClick={() => handleEditDocument(doc)}
                              title="Edit document"
                            >
                              <Edit className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              onClick={() => handleDeleteDocument(doc.id)}
                            >
                              <Trash2 className="h-3 w-3 text-destructive" />
                            </Button>
                          </div>
                        )}
                      </div>
                      <CardTitle className="text-sm line-clamp-2">{doc.title}</CardTitle>
                      <CardDescription className="text-xs line-clamp-2">{doc.description}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {doc.courseFilter && (
                        <Badge variant="default" className="text-xs bg-indigo-600 w-fit">
                          <GraduationCap className="h-3 w-3 mr-1" />
                          {doc.courseFilter}
                        </Badge>
                      )}
                      <div className="text-xs text-muted-foreground space-y-1">
                        <p>By: {getUserNameById(doc.uploadedBy || doc.userId, doc)}</p>
                        <p>Modified: {new Date(doc.dateModified).toLocaleDateString()}</p>
                        {doc.fileSize && <p>Size: {formatFileSize(doc.fileSize)}</p>}
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        {doc.fileUrl && (
                          <Button variant="outline" size="sm" onClick={() => handlePreview(doc)}>
                            <Eye className="h-4 w-4 mr-1" /> Preview
                          </Button>
                        )}
                        <Button variant="outline" size="sm" onClick={() => openDocumentView(doc)}>
                          <FileText className="h-4 w-4 mr-1" /> View
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleDownload(doc)}>
                          <Download className="h-4 w-4 mr-1" /> Download
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {userRole === 'administrator' && (
            <div className="pt-4 border-t border-border/60">
              <DocumentArchiveKMeans
                documents={currentFolderId ? documentsInScope : accessibleDocuments}
                folders={folders}
                scopeLabel={currentFolder ? `"${currentFolder.name}" folder` : 'entire archive'}
              />
            </div>
          )}
        </div>
      ) : (
        /* List View */
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {isSelectMode && (
                      <TableHead className="w-10">
                        <Checkbox
                          checked={allSelectableSelected}
                          onCheckedChange={toggleSelectAll}
                        />
                      </TableHead>
                    )}
                    <TableHead>Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Visibility</TableHead>
                    <TableHead>Size</TableHead>
                    <TableHead>Modified</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {/* Folders first */}
                  {currentFolders.map(folder => (
                    <TableRow key={`folder-${folder.id}`} className={`cursor-pointer hover:bg-muted/50 ${isSelectMode && selectedFolderIds.has(folder.id) ? 'bg-primary/5' : ''}`} onClick={() => isSelectMode ? (canManageItem(folder) && toggleFolderSelection(folder.id)) : navigateToFolder(folder)}>
                      {isSelectMode && (
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          {canManageItem(folder) && (
                            <Checkbox
                              checked={selectedFolderIds.has(folder.id)}
                              onCheckedChange={() => toggleFolderSelection(folder.id)}
                            />
                          )}
                        </TableCell>
                      )}
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <FolderIcon className="h-4 w-4 text-amber-500" />
                          <div>
                            <p className="font-medium text-sm">{folder.name}</p>
                            {folder.description && <p className="text-xs text-muted-foreground">{folder.description}</p>}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell><Badge variant="outline" className="text-xs">Folder</Badge></TableCell>
                      <TableCell>{getFolderVisibilityBadge(folder)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">—</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{new Date(folder.dateModified).toLocaleDateString()}</TableCell>
                      <TableCell>
                        {canManageItem(folder) && (
                          <div className="flex gap-1">
                            {folder.visibility === 'only_me' && userRole === 'administrator' && folder.createdBy === currentUserId && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => { e.stopPropagation(); handleShareFolder(folder); }}
                                title="Share with other admins"
                              >
                                <Share2 className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); handleEditFolder(folder); }}
                              title="Edit folder"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); handleDeleteFolder(folder.id); }}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {/* Documents */}
                  {currentDocuments.map(doc => (
                    <TableRow key={`doc-${doc.id}`} className={isSelectMode && selectedDocIds.has(doc.id) ? 'bg-primary/5' : ''}>
                      {isSelectMode && (
                        <TableCell>
                          {canManageItem(doc) && (
                            <Checkbox
                              checked={selectedDocIds.has(doc.id)}
                              onCheckedChange={() => toggleDocSelection(doc.id)}
                            />
                          )}
                        </TableCell>
                      )}
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-blue-500" />
                          <div>
                            <p className="font-medium text-sm">{doc.title}</p>
                            <p className="text-xs text-muted-foreground truncate max-w-[200px]">{doc.description}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell><Badge variant="outline" className="text-xs">{doc.type}</Badge></TableCell>
                      <TableCell>{getVisibilityBadge(doc.isPublic, doc.targetUsers, doc.allowedRoles, doc.courseFilter)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{doc.fileSize ? formatFileSize(doc.fileSize) : '—'}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{new Date(doc.dateModified).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {doc.fileUrl && (
                            <Button variant="ghost" size="sm" onClick={() => handlePreview(doc)} title="Preview">
                              <Eye className="h-4 w-4" />
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => openDocumentView(doc)} title="View details">
                            <FileText className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDownload(doc)} title="Download">
                            <Download className="h-4 w-4" />
                          </Button>
                          {canManageItem(doc) && (
                            <>
                              <Button variant="ghost" size="sm" onClick={() => handleShareDocument(doc)} title="Share document">
                                <Share2 className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleEditDocument(doc)} title="Edit document">
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteDocument(doc.id)}>
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty state */}
      {currentFolders.length === 0 && currentDocuments.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <Archive className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-medium mb-2">
              {searchTerm ? 'No Results Found' : 'This folder is empty'}
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              {searchTerm
                ? 'Try adjusting your search criteria.'
                : 'Create a folder or upload a document to get started.'}
            </p>
            {!searchTerm && (
              <div className="flex gap-2 justify-center">
                {canCreateInFolder && (
                  <Button variant="outline" onClick={() => setIsCreateFolderOpen(true)}>
                    <FolderPlus className="h-4 w-4 mr-2" />
                    New Folder
                  </Button>
                )}
                {canCreateInFolder && (
                  <Button onClick={() => setIsUploadDocOpen(true)}>
                    <Upload className="h-4 w-4 mr-2" />
                    Upload Document
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Dialogs */}
      {createFolderDialog}
      {uploadDocumentDialog}
      {viewDocumentDialog}
      {editFolderDialog}
      {editDocumentDialog}
      {shareDocumentDialog}
      {shareFolderDialog}
    </div>
  );
}
